import { Container, Text } from 'pixi.js'
import type { Spine } from '@esotericsoftware/spine-pixi-v8'
import gsap from 'gsap'
import { delay } from '../../framework/core/async'
import { layoutMetrics, type Orientation, type Resizable } from '../../framework/render/design'
import { createWinSpine } from '../assets/WinSpine'
import { NumberCounter } from './NumberCounter'

// 報獎 Spine 大小：相對 min(寬,高) 的倍率（套用於 setup 邊界 span）。
// ani_win 邊界含大量透明特效區，可見內容僅約占邊界 44%，故倍率偏大；溢出的透明區在畫面外不影響。
const WIN_SPINE_SIZE = 1.9
// 可見內容底緣相對 root 的下偏量（佔縮放後 span 比例，估自 ani_win 美術）— 用於將金幣堆貼齊操作列。
const WIN_VISUAL_BOTTOM_K = 0.055
// 數字相對 root 的下偏量（佔縮放後 span 比例）— 置於金幣堆一帶，避免遮擋上方老虎/文字動畫。
const WIN_COUNTER_OFFSET_K = 0.05

// 大獎分級名（來自 GameController BIG_WIN_TIERS）→ ani_win Spine 動畫前綴。
function tierAnimKey(tier: string): string {
  const t = tier.toUpperCase()
  if (t.includes('EPIC')) return 'epicwin'
  if (t.includes('SUPER')) return 'superwin'
  if (t.includes('MEGA')) return 'megawin'
  return 'bigwin'
}

// 中獎慶祝表現 — 對應 Unity Slot001_WinUI + BigWinController + WinCelebrationUI + ani_win。
// 一般中獎：金額計數；大獎：ani_win Spine（分級報獎文字含於 Spine 美術）+ 計數。
export class WinCelebration extends Container implements Resizable {
  private readonly counter = new NumberCounter()
  // 乘倍標記（×N）— 對應 Unity 倍數符號飛抵後於贏分面板顯示的倍率強調。
  private readonly multiplierLabel = new Text({
    text: '',
    style: {
      fill: 0xffd34d,
      fontFamily: 'Arial',
      fontWeight: 'bold',
      fontSize: 64,
      stroke: { color: 0x7a3b00, width: 6 },
      dropShadow: { color: 0x000000, blur: 4, distance: 3, alpha: 0.5 },
    },
  })
  private readonly winSpine: Spine | null

  constructor() {
    super()
    this.visible = false
    this.winSpine = createWinSpine()
    if (this.winSpine !== null) {
      this.winSpine.visible = false
      this.addChild(this.winSpine)
    }
    this.multiplierLabel.anchor.set(0.5)
    this.multiplierLabel.visible = false
    this.addChild(this.counter, this.multiplierLabel)
  }

  resize(width: number, height: number, orientation: Orientation): void {
    const cx = width / 2
    this.counter.style.fontSize = Math.max(28, Math.min(width, height) * 0.07)
    this.multiplierLabel.style.fontSize = Math.max(40, Math.min(width, height) * 0.11)

    if (this.winSpine === null) {
      this.counter.position.set(cx, height * 0.7)
      this.positionMultiplierLabel(cx, width, height)
      return
    }

    // 報獎 Spine 置底：
    // ani_win 邊界含大量透明留白且隨動畫幀變動，故改用穩定的 setup 邊界算縮放，
    // 並以 skeleton root（≈視覺基準線）錨定在操作列上方，使報獎落在畫面底部。
    const data = this.winSpine.skeleton.data
    const span = Math.max(data.width, data.height)
    if (span <= 0) return
    const scale = (Math.min(width, height) * WIN_SPINE_SIZE) / span
    this.winSpine.scale.set(scale)
    const scaledSpan = span * scale

    // 底部錨定：金幣堆（可見內容底緣）貼齊操作列上緣；隨縮放自適應，放大也不會被截掉。
    const barHeight = height * layoutMetrics(orientation).barHeight
    const targetBottom = height - barHeight + height * 0.01
    const rootX = cx - (data.x + data.width / 2) * scale // setup 水平中心對齊螢幕中央
    const rootY = targetBottom - scaledSpan * WIN_VISUAL_BOTTOM_K
    this.winSpine.position.set(rootX, rootY)

    // 金額計數下移到金幣堆一帶（root 下方），避免遮擋上方老虎/文字動畫
    this.counter.position.set(cx, rootY + scaledSpan * WIN_COUNTER_OFFSET_K)
    this.positionMultiplierLabel(cx, width, height)
  }

  // ×N 標記置於金額計數上方（不遮擋計數本身）。
  private positionMultiplierLabel(cx: number, width: number, height: number): void {
    this.multiplierLabel.position.set(cx, this.counter.y - Math.min(width, height) * 0.1)
  }

  async showWin(amount: number, duration = 0.8, holdMs = 400): Promise<void> {
    this.visible = true
    this.multiplierLabel.visible = false
    this.counter.setValue(0)
    await this.counter.countTo(amount, duration)
    if (holdMs > 0) await delay(holdMs)
  }

  // 兌獎乘倍第二段：彈出 ×N 標記，並把金額自基礎贏分計數到乘倍後贏分（含放大脈衝強調）。
  // 對應 Unity Slot001_WinUI WIN_UI_SP_COUNT：AppendCallback 後 SetValue(totalLineWin → dTotalWin) 再 StartCount。
  async applyMultiplier(from: number, to: number, multiplier: number, duration = 0.7): Promise<void> {
    this.visible = true
    this.counter.setValue(from)

    this.multiplierLabel.text = `×${multiplier}`
    this.multiplierLabel.visible = true
    gsap.killTweensOf(this.multiplierLabel.scale)
    this.multiplierLabel.scale.set(0)
    gsap.to(this.multiplierLabel.scale, { x: 1, y: 1, duration: 0.3, ease: 'back.out(2.5)' })

    // 計數器放大脈衝（強調倍率生效），yoyo 回到原尺寸。
    gsap.killTweensOf(this.counter.scale)
    gsap.fromTo(
      this.counter.scale,
      { x: 1, y: 1 },
      { x: 1.25, y: 1.25, duration: 0.18, yoyo: true, repeat: 1, ease: 'power2.out' },
    )

    await this.counter.countTo(to, duration)
    await delay(600)
  }

  // 大獎報獎：分級文字已含於 ani_win Spine 美術中，前端依分級播對應動畫 + 金額計數，不再疊程式文字。
  async showBigWin(tier: string, amount: number): Promise<void> {
    this.visible = true
    this.counter.setValue(0)
    this.playTierSpine(tier)
    await this.counter.countTo(amount, 1.4)
    await delay(700)
  }

  // 依分級播放對應 Spine 動畫：{key}_in（進場一次）→ {key}_loop（循環）。
  private playTierSpine(tier: string): void {
    if (this.winSpine === null) return
    this.winSpine.visible = true
    const names = this.winSpine.skeleton.data.animations.map((a) => a.name)
    const key = tierAnimKey(tier)
    const intro = `${key}_in`
    const loop = `${key}_loop`
    if (names.includes(intro)) {
      this.winSpine.state.setAnimation(0, intro, false)
      if (names.includes(loop)) this.winSpine.state.addAnimation(0, loop, true, 0)
    } else if (names.length > 0) {
      this.winSpine.state.setAnimation(0, names[0], true)
    }
  }

  hide(): void {
    this.visible = false
    this.counter.killTween()
    gsap.killTweensOf(this.counter.scale)
    gsap.killTweensOf(this.multiplierLabel.scale)
    this.counter.scale.set(1)
    this.multiplierLabel.visible = false
    if (this.winSpine !== null) this.winSpine.visible = false
  }
}
